/* Unified home page content — organiser-led positioning (2026 brand pass) */

export const HERO = {
  badge: 'FOR SOCIETIES, TRIPS, CHARITY DAYS & CLUB COMPS',
  headline: 'Run any golf day like a tour event.',
  subheadline:
    "Caddie Live helps you create golf events, capture live scores, and share leaderboards — for societies, charity days, club comps and friendly roll-ups. Without the spreadsheet chaos.",
  ctaPrimary: 'Create a free event',
  ctaSecondary: 'See how it works',
  trustLine:
    'Built for real golf days — Stableford, stroke play, team formats, live scoring, leaderboards.',
  trust: [
    'Free for small groups',
    'No download needed today',
    'Available on iPhone & the web',
  ],
  deviceNote:
    "Works on any phone via the web today. Native iPhone and Android apps are on the way.",
};

/* ── Problem section — make organisers feel understood ── */
export const PROBLEM = {
  label: 'IF YOU ORGANISE GOLF DAYS, YOU KNOW',
  heading: 'Golf days are supposed to be fun. Running them often isn\'t.',
  subheading:
    'You shouldn\'t need a spreadsheet, three group chats and a calculator just to find out who won.',
  cards: [
    {
      icon: 'chat',
      title: 'Chasing scores on WhatsApp',
      desc: '"What did you shoot on 7?" times twelve. Half the answers come in after the prize-giving.',
    },
    {
      icon: 'description',
      title: 'Paper cards going missing',
      desc: 'One illegible card, one card left in a buggy, one card the dog ate. The maths starts at the bar.',
    },
    {
      icon: 'calculate',
      title: 'Doing the Stableford by hand',
      desc: 'Shot allowances, strokes received, half points — someone\'s always doing it on a napkin.',
    },
    {
      icon: 'gavel',
      title: 'Countback arguments',
      desc: 'Last six, last three, last hole. Two players tied on 38 and nobody can agree who wins the voucher.',
    },
  ],
};

/* ── Solution section — Caddie Live keeps the competition moving ── */
export const SOLUTION = {
  label: 'THE FIX',
  heading: 'Caddie Live keeps the competition moving.',
  subheading:
    'One link, one leaderboard, one source of truth. Players score on their phones, the leaderboard updates live, and the winner is decided before anyone reaches the bar.',
};

export const TRUST_STATS = [
  { icon: 'format_list_numbered', label: '18+ Formats' },
  { icon: 'groups', label: 'Built for Full-Field Events' },
  { icon: 'tv', label: 'Live TV Mode' },
  { icon: 'calculate', label: 'Handicap-Aware Scoring' },
];

export const FREE_FEATURES = {
  label: 'EVERY GOLF DAY, SORTED',
  heading: "Everything a golf day needs. Nothing a golf day doesn't.",
  subheading:
    'Every format you play, a live leaderboard on every phone, and set-up in five minutes. Caddie Live is the operating system for the way real golf days run — societies, trips, charity days, club comps.',
  cards: [
    {
      icon: 'leaderboard',
      title: 'A live leaderboard on every phone',
      desc: "Every player scores on their phone. Every phone shows the leaderboard. The clubhouse TV shows it too. Ties, countbacks and prizes sorted before the 18th green.",
    },
    {
      icon: 'layers',
      title: 'Every format you actually play',
      desc: 'Stableford, Texas Scramble, Matchplay, Skins, Nassau, Greensomes, Order of Merit — 18+ formats in the box. Mix formats across a trip, a season or a single day.',
    },
    {
      icon: 'bolt',
      title: 'Set up in five minutes',
      desc: 'Add players, pick a format, set the prizes — done. Handicaps, shot allowances and countbacks are worked out for you.',
    },
    {
      icon: 'share',
      title: 'Share the results',
      desc: 'Final standings, shareable cards, printable PDF. Send the result before anyone reaches the car park.',
    },
  ],
  ctaStrip: 'Create your event — it takes five minutes',
};

export const HANDICAP_CARD = {
  /* Preserved — consumed by /handicap-tracker in Phase 3. Removed from homepage JSX in Phase 2. */
  label: 'DIGITAL HANDICAP CARD',
  heading: 'Your playing index. Always with you.',
  subheading:
    'Track every casual round, build your handicap from real data, and carry a digital card that proves your game. Share it with any pro shop or organiser.',
  features: [
    { title: 'Auto-Calculated CPI', desc: 'Your Current Playing Index (CPI) is calculated from your scored rounds. Play more, get a more accurate number.' },
    { title: 'Shareable Digital Card', desc: 'Share your handicap card via a link or download a printable PDF. Show it at check-in, the first tee, or any time someone asks your handicap.' },
    { title: 'Verification Page', desc: 'Every card has a QR code linking to a public verification page showing your index and qualifying rounds.' },
    { title: 'Trend Tracking', desc: 'See how your handicap changes over time. Every round that counts is visible in your calculation breakdown.' },
  ],
  pricingNote: 'Free while in beta — including the digital card. Premium ways to carry it are coming later, with fair notice first.',
  freeIncludes: 'Free includes: handicap calculation, trend chart, round history, digital card',
  cardAdds: 'Coming soon: Apple Wallet card and printable PDF with full round history',
  disclaimer:
    "CPI (Current Playing Index) is calculated using the same method as the World Handicap System, but it is not an official WHS or federation-issued handicap. It's Caddie Live's own index, based on the rounds you choose to count.",
};

export const GAME_FORMATS = {
  label: 'EVERY FORMAT YOU PLAY',
  heading: 'Stableford. Scramble. Skins. And everything else.',
  subheading:
    '18+ formats built in. Pick yours, set the handicap rules, and go. Mix formats across a trip, a season, or a single day.',
  cards: [
    { slug: 'stroke-play',          icon: 'golf_course',  name: 'Stroke Play',           desc: 'Count every stroke, gross or net. Handicap-adjusted with automatic countback.', tags: ['GROSS', 'NET', 'HANDICAPS'] },
    { slug: 'stableford',           icon: 'analytics',    name: 'Stableford',             desc: 'Points per hole — the go-to format for society golf across Europe.',             tags: ['POINTS', 'HANDICAPS'] },
    { slug: 'fourball-better-ball', icon: 'groups',       name: 'Fourball Better Ball',   desc: 'Two players, one team. Best score counts each hole.',                            tags: ['TEAMS', '2-PLAYER'] },
    { slug: 'texas-scramble',       icon: 'handshake',    name: 'Texas Scramble',         desc: 'Everyone hits, pick the best, play from there. The ultimate team format.',       tags: ['TEAMS', '2-4 PLAYERS'] },
    { slug: 'matchplay',            icon: 'swords',       name: 'Matchplay',              desc: 'Head-to-head, hole by hole. The original format of competition golf.',           tags: ['1V1', 'HOLES WON'] },
    { slug: 'skins',                icon: 'payments',     name: 'Skins',                  desc: 'Win the hole, win the skin. Carried skins roll over to the next hole.',          tags: ['BETTING', 'CARRY OVER'] },
    { slug: 'nassau',               icon: 'view_timeline',name: 'Nassau',                 desc: 'Three bets in one: front nine, back nine, and overall.',                         tags: ['SIDE BETS', 'CLASSIC'] },
    { slug: 'wolf',                 icon: 'pets',         name: 'Wolf',                   desc: 'Rotating 4-player game. Choose your partner or go lone wolf.',                   tags: ['4-PLAYER', 'STRATEGY'] },
    { slug: 'par-bogey',            icon: 'flag',         name: 'Par / Bogey',            desc: 'Win, lose, or halve each hole against par.',                                      tags: ['VS PAR', 'HANDICAPS'] },
    { slug: 'modified-stableford',  icon: 'rocket_launch',name: 'Modified Stableford',    desc: 'Big bonuses for birdies and eagles, penalties for bogeys.',                      tags: ['POINTS', 'AGGRESSIVE'] },
    { slug: 'vegas',                icon: 'casino',       name: 'Vegas',                  desc: '2v2. Combine scores into two-digit numbers. Birdie flip changes everything.',     tags: ['TEAMS', 'BETTING'] },
    { slug: 'greensomes-foursomes', icon: 'swap_horiz',   name: 'Greensomes / Foursomes', desc: 'Alternate shot formats. Both drive, pick the best, then alternate.',             tags: ['TEAMS', 'ALTERNATE'] },
  ],
  footer:
    'Every format supports individual and team play, with handicaps and shot allowances applied correctly.',
};

export const FOR_ORGANISERS = {
  label: 'FOR ORGANISERS & SOCIETIES',
  heading: 'Run your competition like a pro.',
  subheading: 'Tour-feel. No tour budget. Set up in minutes. Players score on their phones. The leaderboard updates live.',
  cards: [
    { icon: 'trophy',       title: 'Every Format You Play',             desc: 'Stableford, Stroke Play, Matchplay, Texas Scramble, Skins, Nassau, Greensomes, Order of Merit — and more.' },
    { icon: 'groups',       title: 'Built for Full-Field Events',       desc: 'From a 12-player society day to a full-field charity day. Flights, teams and shotgun starts all handled. QR code join.' },
    { icon: 'leaderboard',  title: 'A Live Leaderboard on Every Phone', desc: 'Real-time updates on every phone in the field. Clubhouse TV, spectator link or shared URL — one source of truth.' },
    { icon: 'event_repeat', title: 'Multi-Round Series',                desc: 'Season-long series and away trips with cumulative standings, best-of-N scoring and a year-end order of merit.' },
    { icon: 'tv',           title: 'TV / Broadcast Mode',               desc: 'Broadcast-quality display for the clubhouse. Auto-scrolling, sponsor branding, Ryder-Cup-worthy presentation.' },
    { icon: 'upload_file',  title: 'CSV Import & Grouping',             desc: "Import last year's roster in seconds. Auto-flight by handicap or pick your own groupings and tee times." },
    { icon: 'payments',     title: 'Entry Fee Collection',              desc: 'Set a fee and players pay as they join — powered by Stripe, straight to your own bank. The roster shows who has paid; receipts go out automatically.' },
  ],
  /* No arrow in the label. AudienceLandingPage renders this beside <Arrow />,
     so the arrow was appearing twice — "Create a free event → →". The component
     owns the arrow; the string owns the words. The six locale bundles carried
     the same doubled arrow and were fixed with it. */
  ctaPrimary: 'Create a free event',
  ctaSub: 'Every feature is free while we’re in beta. No card needed.',
};

export const HOW_IT_WORKS_HEADING = 'Four steps. Five minutes. Done.';

export const HOW_IT_WORKS = [
  { n: '1', name: 'Create the event',  desc: 'Name your day, pick the course and choose the format — Stableford, stroke play, scramble, skins or a mix. Under two minutes.' },
  { n: '2', name: 'Add players',       desc: 'Import a CSV, paste a list, or share a join link. Handicaps come with them.' },
  { n: '3', name: 'Players score',     desc: 'Players tap in scores hole by hole. If signal drops, scores sync when it\'s back.' },
  { n: '4', name: 'Everyone follows',  desc: 'A live leaderboard on every phone, the clubhouse TV, and a public spectator link.' },
];

/* ── Benefit-grouped feature highlights (homepage) ── */
export const WHAT_YOU_GET = [
  { icon: 'leaderboard',  title: 'Live leaderboard on every phone', desc: 'Players, organisers, spectators — one source of truth, updating in real time.' },
  { icon: 'layers',       title: 'Every format you actually play',  desc: 'Stableford, stroke play, scramble, matchplay, skins, greensomes, order of merit — 18+ in the box.' },
  { icon: 'calculate',    title: 'Handicap-aware scoring',          desc: 'Net and gross calculated automatically. Shot allowances, countbacks and tiebreaks done for you.' },
  { icon: 'emoji_events', title: 'Multi-round events & series',     desc: 'Run a season, a multi-day trip, or a one-off charity day — all from a single event.' },
  { icon: 'share',        title: 'Shareable results',               desc: 'Final standings, printable PDF, share links for the WhatsApp group and the newsletter.' },
  { icon: 'tv',           title: 'TV / broadcast leaderboard',      desc: 'Full-screen mode for the clubhouse TV. Auto-scrolling, themed, with sponsor branding.' },
];

export const AI_FEATURES = {
  label: 'ALSO IN THE BOX',
  heading: 'On-course extras when you need them.',
  askCaddie: {
    icon: 'chat',
    title: 'Ask Caddie — quick rulings',
    desc: "Drop, penalty, free relief? Get a plain-English answer grounded in R&A rules — useful mid-round when the group can't agree.",
    pills: ['R&A Sourced', 'Plain English', 'Photo Rulings'],
  },
  caddieCoach: {
    icon: 'psychology',
    title: 'Round review',
    desc: "After the round, see where the shots went and what defined it. A quick debrief, not a coaching course.",
    pills: ['Post-Round Analysis', 'Scoring Trends', 'Progress Over Time'],
  },
  footer: '2 free AI queries per day. More coming with premium, later.',
};

export const USE_CASES = [
  {
    title: 'Golf societies',
    desc: "Monthly meetings, the Christmas rollup, the annual away trip. One place for every fixture, every result, every order-of-merit point — with no Sunday-night spreadsheet.",
    href: '/golf-societies',
    cta: 'See it for societies →',
  },
  {
    title: 'Charity golf days',
    desc: 'Sponsor logos on the leaderboard, raffle results in the same place, and a clubhouse TV showing live standings. Donors stay; sponsors come back next year.',
    href: '/charity-golf-days',
    cta: 'See it for charity days →',
  },
  {
    title: 'Club roll-ups & comps',
    desc: "Captain's day, weekend medals, midweek rollups. A clean live leaderboard on the clubhouse TV — without printing a thing.",
    href: '/golf-societies',
    cta: 'See it for clubs →',
  },
  {
    title: 'Weekend groups & friends',
    desc: 'The same four lads, every Saturday. Or a one-off birthday round. Score it, settle it, share it — without anyone doing maths in the car park.',
    href: '/track-your-game',
    cta: 'See it for groups →',
  },
  {
    title: 'Corporate golf days',
    desc: 'Clients, partners, the team off-site. A polished, live competition that makes the day feel like the event your guests were promised.',
    href: '/charity-golf-days',
    cta: 'See it for corporates →',
  },
  {
    title: 'Match trips & golf tours',
    desc: 'Three days, four courses, twelve lads, one trophy. Caddie Live runs the tour so you can actually play the tour.',
    href: '/golf-trips',
    cta: 'See it for trips →',
  },
];

/* ── Founder / community section — authentic without sounding unfinished ── */
export const FOUNDER = {
  label: 'BUILT BY A GOLFER',
  heading: 'Built by a golfer. Shaped by the golfers who use it.',
  body:
    'Caddie Live started as a side project to make golf days easier to run, more fun to follow, and less dependent on WhatsApp, paper cards, and someone doing countback in the clubhouse with a pint in hand.',
  body2:
    'It\'s built independently, improving every week, and shaped by feedback from real organisers, real societies, and real golfers — including the ones who told us when we got it wrong.',
  pillars: [
    { icon: 'verified',   title: 'Independent & focused', desc: 'No investors. No bloat. One product, built properly.' },
    { icon: 'forum',      title: 'Shaped by golfers',     desc: 'Every format, every fix, every feature comes from real days.' },
    { icon: 'rocket_launch', title: 'Improving every week', desc: 'Small team, fast cadence. The product you see today is not the product you\'ll see in three months.' },
  ],
};

export const FAQ = [
  {
    q: 'Is Caddie Live free?',
    a: "Yes — free for personal rounds and small casual events. When your golf days get more serious (full-field events, season-long order of merit, custom branding) there are paid plans starting from a single one-off event fee. You only pay when you need more.",
  },
  {
    q: 'What formats are supported?',
    a: '18+ and counting. Stableford, Stroke Play, Modified Stableford, Texas Scramble, Matchplay, Fourball Better Ball, Greensomes, Foursomes, Skins, Nassau, Wolf, Vegas, Order of Merit — and more. Every format supports individual and team play with handicaps applied automatically.',
  },
  {
    q: 'Do players need to download an app?',
    a: "No. Players score on their phone via a web link — no download needed today. Native iPhone and Android apps are on the way for organisers who want extra control, but the web experience is here to stay.",
  },
  {
    q: 'Can I use it for a society day?',
    a: "Yes — societies are exactly who Caddie Live is built for. Run your monthly meeting, the Christmas rollup, the annual away trip, and a season-long order of merit, all from one place.",
  },
  {
    q: 'Can I use it for a charity event?',
    a: "Yes. Charity days work brilliantly on Caddie Live: live scoring keeps the room engaged, sponsors get logos on the leaderboard and the clubhouse TV, and the prize-giving runs on time because the results are already in.",
  },
  {
    q: 'Does it replace paper scorecards?',
    a: "Yes. Players score on their phone hole by hole, scores sync as soon as connection returns if signal drops, and any player in the group can score for the fourball — so a flat battery isn\'t a blocker. No clipboard, no maths in the bar.",
  },
  {
    q: 'Is it suitable for clubs?',
    a: "Yes. Clubs use Caddie Live for captain\'s days, weekend medals, midweek rollups, charity days and away matches — with custom branding, TV mode for the clubhouse, and a season-on-season archive. Talk to us about a Clubs plan if you want bespoke setup.",
  },
  {
    q: 'What about handicaps?',
    a: "Import a roster with handicaps, pull them from each player\'s Caddie Live playing handicap, or enter them manually. We handle shot allowances for every format automatically.",
  },
  {
    q: 'Who is building Caddie Live?',
    a: "Caddie Live is built independently by a golfer who got tired of running golf days from a spreadsheet. It\'s a small, focused team improving the product every week, shaped by feedback from the societies, organisers and clubs who use it. No investors, no bloat — just a product built properly for the way real golf days run.",
  },
];

export const FINAL_CTA = {
  heading: "Ready to make your next golf day feel bigger?",
  subheading:
    "No clipboard. No spreadsheet. No WhatsApp score chase. Just a live leaderboard, every format you play, and five minutes of setup.",
  ctaPrimary: 'Create your first event',
  ctaSecondary: 'See how it works',
  trust: ['Free while in beta', 'Live leaderboards', 'Handicap-aware scoring', 'Thousands of courses'],
};
